// ── ThriveLonger.ai HIPAA Compliance & Data Protection Policy ──
// Shared by the Medical Records consent dialog and the About page (#hipaa).
// Keep POLICY_VERSION in sync with backend/routes/phi/consent.py.

import { CONTACT_EMAIL } from './contact';

export const HIPAA_POLICY_VERSION = '2026-08-01';

export const HIPAA_DISCLAIMER =
  'ThriveLonger.ai is a health & longevity optimization platform, not a healthcare ' +
  'provider, and the information on this site is not medical advice. By downloading ' +
  'medical information templates or uploading medical records, medications, diagnoses, ' +
  'or procedure histories, you are voluntarily sharing Protected Health Information (PHI) ' +
  'with ThriveLonger.ai so we can safeguard it in your encrypted vault and personalize ' +
  'your insights. Always consult a licensed physician before acting on any information ' +
  'or AI-generated insight provided by this platform.';

export const HIPAA_POLICY_SECTIONS = [
  {
    id: 'commitment',
    title: 'Our HIPAA Commitment',
    body:
      'ThriveLonger.ai maintains administrative, physical, and technical safeguards ' +
      'modeled on the HIPAA Security Rule (45 CFR §164.302–318) and the Privacy Rule. ' +
      'Every system that touches member health information is designed so that ' +
      'plaintext PHI never persists outside your encrypted vault.',
  },
  {
    id: 'encryption',
    title: 'Encryption — At Rest & In Transit',
    body:
      'Every medical record you upload is encrypted the moment it reaches our servers ' +
      'using AES-256-GCM envelope encryption: each file gets its own unique data key, ' +
      'which is itself wrapped by a master key that is never stored alongside your data. ' +
      'All traffic between your browser and our servers is protected with TLS. Downloads ' +
      'use single-use signed links that expire within minutes.',
  },
  {
    id: 'anonymization',
    title: 'Anonymization & De-Identification',
    body:
      'When your records are processed for AI insights, we work from de-identified ' +
      'extracts: direct identifiers (name, email, addresses, contact details) are ' +
      'stripped or pseudonymized before analysis, consistent with the HIPAA Safe Harbor ' +
      'de-identification standard (45 CFR §164.514(b)(2)). AI-generated summaries are ' +
      'stored back inside your encrypted vault, and your identifiable data is never ' +
      'sold or shared with advertisers or data brokers.',
  },
  {
    id: 'access',
    title: 'Access Controls & Audit Logging',
    body:
      'Access to PHI follows the minimum-necessary principle: only you (and staff you ' +
      'explicitly authorize) can open your records. Every view, upload, download, and ' +
      'deletion is written to an append-only audit log — required by HIPAA §164.312(b) — ' +
      'that records who accessed what, when, and from where. Idle sessions are ' +
      'automatically signed out.',
  },
  {
    id: 'rights',
    title: 'Your Rights & Control',
    body:
      'Your records belong to you. You can download or permanently delete any record at ' +
      'any time from the Medical Records page; deletions purge the encrypted payload ' +
      'immediately. You may also withdraw consent for future uploads at any time by ' +
      `contacting ${CONTACT_EMAIL}.`,
  },
  {
    id: 'breach',
    title: 'Breach Notification',
    body:
      'In the unlikely event of a security incident affecting your health information, ' +
      'we follow the HIPAA Breach Notification Rule (45 CFR §164.400–414): affected ' +
      'members are notified without unreasonable delay and no later than 60 days after ' +
      'discovery, with a clear description of what happened and what we are doing about it.',
  },
];

// Short bullets used in the consent dialog.
export const HIPAA_POLICY_SUMMARY = [
  'AES-256-GCM envelope encryption for every record, plus TLS in transit.',
  'De-identified processing — direct identifiers are stripped before AI analysis.',
  'Append-only audit log of every access, per HIPAA §164.312(b).',
  'Single-use, minutes-long expiring download links.',
  'You can download or permanently delete your records at any time.',
  'Your data is never sold or shared with advertisers or data brokers.',
];
